import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import { useVeCusto } from '../../design/sigilo';
import { emReais } from '../../encomendas';
import {
  ROTULO_PAGAMENTO,
  ROTULO_RECEITA,
  ROTULO_RESOLUCAO,
  dataHoraBr,
  desfazer,
  lerEncomenda,
  pagar,
  resolver,
  resolverDivergencia,
  telefoneBr,
} from '../../encomendas-fluxo';
import type { DetalheDaEncomenda } from '../../encomendas-fluxo';
import { Campo, Gaveta, Opcoes } from './Gaveta';

/**
 * O DETALHE de uma encomenda (fase 4): timeline estilo e-commerce, itens com o andamento (6/10),
 * cliente, pagamento (pago e saldo do servidor) e as ações do balconista — Resolver encomenda e
 * Registrar pagamento. Quem tem `encomendas.vincular` vê também os vínculos (desfazer com motivo)
 * e resolve divergência.
 */

const ROTULO_DO_ESTADO: Readonly<Record<string, string>> = {
  SOLICITADO: 'Aguardando pedido',
  EM_COMPRA: 'Pedido em andamento',
  COMPRADO: 'Comprado',
  COMPRA_PARCIAL: 'Comprado em parte',
  EM_FALTA: 'Em falta',
  FATURADO: 'Faturado · a caminho',
  FATURADO_PARCIAL: 'Faturado em parte',
  RECEBIDO: 'Chegou',
  RECEBIDO_PARCIAL: 'Chegou em parte',
  DIVERGENCIA: 'Divergência',
  CONFERIDO: 'Conferido',
  ENTREGUE: 'Entregue',
  CANCELADO: 'Cancelado',
  CLIENTE_DESISTIU: 'Cliente desistiu',
};

export function Detalhe({
  sessao,
  id,
  podeVincular,
  aoFechar,
  aoMudar,
}: {
  sessao: Sessao;
  id: string;
  podeVincular: boolean;
  aoFechar: () => void;
  aoMudar: () => void;
}) {
  const veCusto = useVeCusto();
  const [enc, setEnc] = useState<DetalheDaEncomenda | null>(null);
  const [falha, setFalha] = useState<string | null>(null);
  const [acao, setAcao] = useState<'resolver' | 'pagar' | null>(null);
  const carregar = useCallback(() => {
    void lerEncomenda(id).then((r) => {
      if (r.ok) setEnc(r.dados.encomenda);
      else setFalha('Não foi possível abrir esta encomenda.');
    });
  }, [id]);
  useEffect(() => {
    carregar();
    // aberta, a encomenda se atualiza sozinha (o servidor puxa pedido e nota na hora)
    const t = window.setInterval(carregar, 20_000);
    return () => window.clearInterval(t);
  }, [carregar]);
  const depois = () => {
    setAcao(null);
    carregar();
    aoMudar();
  };

  if (!enc)
    return (
      <Gaveta titulo="Encomenda" aoFechar={aoFechar}>
        {falha ? (
          <p className="enc-erro">{falha}</p>
        ) : (
          <div className="enc-lista-esq enc-esqueleto">
            <i />
            <i />
            <i />
          </div>
        )}
      </Gaveta>
    );

  const aberta = enc.resolucao === null;
  const saldo = enc.pagamento.saldo === null ? null : Number(enc.pagamento.saldo);
  return (
    <Gaveta
      titulo={enc.codigo}
      subtitulo={
        <>
          <span className={`enc-macro enc-macro-${enc.situacao.macro.toLowerCase()}`}>
            {enc.situacao.rotulo}
          </span>
          {enc.urgente && <span className="enc-tag enc-tag-urgente">Urgente</span>}
          <span>
            Criada por {enc.criadoPor.nome} · {dataHoraBr(enc.criadoEm)}
          </span>
        </>
      }
      aoFechar={aoFechar}
      largura={720}
      rodape={
        aberta ? (
          <>
            {saldo !== null && saldo > 0 && (
              <button type="button" className="enc-botao-sec" onClick={() => setAcao('pagar')}>
                Registrar pagamento
              </button>
            )}
            <button type="button" className="enc-botao" onClick={() => setAcao('resolver')}>
              Resolver encomenda
            </button>
          </>
        ) : undefined
      }
    >
      {aberta && enc.situacao.proximoPasso && (
        <div className={`enc-proximo${enc.situacao.acaoDoBalconista ? ' enc-proximo-acao' : ''}`}>
          <span>{enc.situacao.acaoDoBalconista ? 'AÇÃO NECESSÁRIA' : 'Próximo passo'}</span>
          <b>{enc.situacao.proximoPasso}</b>
        </div>
      )}

      <ol className="enc-timeline" aria-label="Andamento">
        {enc.timeline.map((p) => (
          <li key={p.passo} className={p.feito ? 'enc-tl-feito' : p.atual ? 'enc-tl-atual' : ''}>
            <i aria-hidden="true">
              {p.feito ? <Icone nome="check" tamanho={12} traco={2.4} /> : null}
            </i>
            <div>
              <b>{p.passo}</b>
              <span>
                {p.em ? dataHoraBr(p.em) : p.atual ? 'agora' : ''}
                {p.detalhe ? ` — ${p.detalhe}` : ''}
              </span>
            </div>
          </li>
        ))}
      </ol>

      <section className="enc-secao-detalhe">
        <h3>Produtos</h3>
        {enc.itens.map((i) => (
          <div key={i.id} className="enc-item-detalhe">
            <div className="enc-linha-topo">
              <b>{i.descricao}</b>
              <span className="enc-tag">{ROTULO_DO_ESTADO[i.estado] ?? i.estado}</span>
            </div>
            <div className="enc-linha-detalhe">
              <span>{String(i.quantidade)} un</span>
              {i.controlado && <span className="enc-tag enc-tag-atencao">Controlado</span>}
              <span>
                Sugerido: {i.fornecedorConsultado ?? '—'}
                {veCusto && i.custoConsultado ? ` · ${emReais(i.custoConsultado)}` : ''}
              </span>
              {i.fornecedorComprado && (
                <span>
                  Comprado: <b>{i.fornecedorComprado}</b>
                  {veCusto && i.custoEfetivo ? ` · ${emReais(i.custoEfetivo)}` : ''}
                </span>
              )}
              <span>
                Venda {emReais(i.precoInova)} · PMC {emReais(i.pmc)}
              </span>
            </div>
          </div>
        ))}
      </section>

      <section className="enc-secao-detalhe enc-duas">
        <div>
          <h3>Cliente</h3>
          <p>
            <b>{enc.cliente.nome}</b>
            <br />
            <a href={`https://wa.me/55${enc.cliente.telefone}`} target="_blank" rel="noreferrer">
              {telefoneBr(enc.cliente.telefone)}
            </a>
            {enc.cliente.cpf && (
              <>
                <br />
                CPF {enc.cliente.cpf}
              </>
            )}
          </p>
          <p>
            {enc.modalidade === 'ENTREGA' && enc.endereco ? (
              <>
                <b>Entrega</b>
                <br />
                {enc.endereco.logradouro}, {enc.endereco.numero}
                {enc.endereco.complemento ? ` — ${enc.endereco.complemento}` : ''}
                <br />
                {enc.endereco.bairro}
                {enc.endereco.referencia ? ` · ${enc.endereco.referencia}` : ''}
              </>
            ) : (
              <b>Retirada na loja</b>
            )}
          </p>
        </div>
        <div>
          <h3>Pagamento</h3>
          <p>
            <b>{ROTULO_PAGAMENTO[enc.pagamento.situacao] ?? enc.pagamento.situacao}</b>
            <br />
            Combinado {emReais(enc.pagamento.valorTotalCombinado)} · Pago{' '}
            {emReais(enc.pagamento.pago)}
            {enc.pagamento.saldo !== null && (
              <>
                <br />
                <b className={saldo && saldo > 0 ? 'enc-n-zero' : 'enc-n-ok'}>
                  Saldo {emReais(enc.pagamento.saldo)}
                </b>
              </>
            )}
          </p>
          <p>
            Receita: {ROTULO_RECEITA[enc.receita.situacao] ?? enc.receita.situacao}
            {enc.receita.obs ? ` — ${enc.receita.obs}` : ''}
          </p>
          {enc.prazo.em && (
            <p>
              Prazo prometido: <b>{dataHoraBr(enc.prazo.em)}</b> (por{' '}
              {enc.prazo.informadoPor ?? '—'})
            </p>
          )}
        </div>
      </section>

      {enc.observacao && (
        <section className="enc-secao-detalhe">
          <h3>Observação</h3>
          <p>{enc.observacao}</p>
        </section>
      )}

      {enc.resolucao && (
        <section className="enc-secao-detalhe">
          <h3>Resolvida</h3>
          <p>
            {ROTULO_RESOLUCAO[enc.resolucao.como] ?? enc.resolucao.como} ·{' '}
            {dataHoraBr(enc.resolucao.em)} · por {enc.resolucao.por}
            {enc.resolucao.quemRecebeu && (
              <>
                <br />
                Quem recebeu: {enc.resolucao.quemRecebeu}
              </>
            )}
            {enc.resolucao.obs && (
              <>
                <br />
                {enc.resolucao.obs}
              </>
            )}
          </p>
        </section>
      )}

      {podeVincular && <Vinculos enc={enc} sessao={sessao} aoMudar={depois} />}

      {acao === 'resolver' && (
        <Resolver sessao={sessao} enc={enc} aoFechar={() => setAcao(null)} aoResolver={depois} />
      )}
      {acao === 'pagar' && (
        <Pagar sessao={sessao} enc={enc} aoFechar={() => setAcao(null)} aoPagar={depois} />
      )}
    </Gaveta>
  );
}

function Vinculos({
  enc,
  sessao,
  aoMudar,
}: {
  enc: DetalheDaEncomenda;
  sessao: Sessao;
  aoMudar: () => void;
}) {
  const [erro, setErro] = useState<string | null>(null);
  // a ação pendente pede o motivo NA TELA (fica na trilha), nunca numa caixa do navegador
  const [pedindo, setPedindo] = useState<
    | { tipo: 'desfazer'; vinculo: 'pedido' | 'nf'; id: string; titulo: string }
    | { tipo: 'divergencia'; id: string; acao: string; titulo: string }
    | null
  >(null);
  const [texto, setTexto] = useState('');
  const vivosP = enc.vinculos.pedidos.filter((v) => !v.desfeito);
  const vivosN = enc.vinculos.notas.filter((v) => !v.desfeito);
  const abertas = enc.divergencias.filter((d) => !d.resolvida);
  const confirmar = async () => {
    if (!pedindo || texto.trim().length < 5) return setErro('Escreva pelo menos 5 letras.');
    const r =
      pedindo.tipo === 'desfazer'
        ? await desfazer(sessao, pedindo.vinculo, pedindo.id, texto.trim())
        : await resolverDivergencia(sessao, pedindo.id, pedindo.acao, texto.trim());
    if (r.ok) {
      setPedindo(null);
      setTexto('');
      setErro(null);
      aoMudar();
    } else
      setErro(
        r.falha.tipo === 'conflito' || r.falha.tipo === 'invalido'
          ? r.falha.mensagem
          : 'Não foi possível salvar.',
      );
  };
  if (!vivosP.length && !vivosN.length && !abertas.length) return null;
  return (
    <section className="enc-secao-detalhe">
      <h3>Vínculos (admin)</h3>
      {vivosP.map((v) => (
        <div key={v.id} className="enc-vinculo">
          <span>
            Pedido <b>#{v.pedido}</b> · {v.fornecedor ?? '—'} · {String(v.quantidade)} un ·{' '}
            {v.modo === 'AUTOMATICO' ? `automático (${v.confianca.toLowerCase()})` : 'manual'}
          </span>
          <button
            type="button"
            className="enc-link"
            onClick={() =>
              setPedindo({
                tipo: 'desfazer',
                vinculo: 'pedido',
                id: v.id,
                titulo: `Desfazer o pedido #${v.pedido}`,
              })
            }
          >
            Desfazer
          </button>
        </div>
      ))}
      {vivosN.map((v) => (
        <div key={v.id} className="enc-vinculo">
          <span>
            NF <b>{v.numero ?? '—'}</b> · {v.fornecedor ?? '—'} · {String(v.quantidade)} un ·{' '}
            {v.fonte}
            {v.recebidaEm ? ` · chegou ${dataHoraBr(v.recebidaEm)}` : ''}
          </span>
          <button
            type="button"
            className="enc-link"
            onClick={() =>
              setPedindo({
                tipo: 'desfazer',
                vinculo: 'nf',
                id: v.id,
                titulo: `Desfazer a NF ${v.numero ?? ''}`,
              })
            }
          >
            Desfazer
          </button>
        </div>
      ))}
      {abertas.map((d) => (
        <div key={d.id} className="enc-vinculo enc-vinculo-problema">
          <span>
            Divergência <b>{d.tipo.replace('_', ' ').toLowerCase()}</b> · esperado{' '}
            {String(d.esperada ?? '—')}, recebido {String(d.recebida ?? '—')}
            {d.obs ? ` · ${d.obs}` : ''}
          </span>
          <span className="enc-vinculo-acoes">
            {(
              [
                ['AGUARDAR_COMPLEMENTO', 'Aguardar o resto'],
                ['CLIENTE_ACEITOU_MENOS', 'Cliente aceitou o que veio'],
                ['OUTRO', 'Outro'],
              ] as const
            ).map(([acao, rotulo]) => (
              <button
                key={acao}
                type="button"
                className="enc-link"
                onClick={() => setPedindo({ tipo: 'divergencia', id: d.id, acao, titulo: rotulo })}
              >
                {rotulo}
              </button>
            ))}
          </span>
        </div>
      ))}
      {pedindo && (
        <div className="enc-motivo">
          <b>{pedindo.titulo}</b>
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={2}
            placeholder={
              pedindo.tipo === 'desfazer'
                ? 'Por que desfazer? (fica na trilha)'
                : 'Como foi resolvido? (fica na trilha)'
            }
            autoFocus
          />
          {erro && <p className="enc-form-erro">{erro}</p>}
          <div>
            <button
              type="button"
              className="enc-botao-sec"
              onClick={() => {
                setPedindo(null);
                setErro(null);
              }}
            >
              Voltar
            </button>
            <button type="button" className="enc-botao" onClick={() => void confirmar()}>
              Confirmar
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Resolver({
  sessao,
  enc,
  aoFechar,
  aoResolver,
}: {
  sessao: Sessao;
  enc: DetalheDaEncomenda;
  aoFechar: () => void;
  aoResolver: () => void;
}) {
  const pronta = enc.situacao.macro === 'PRONTA';
  const [como, setComo] = useState<string>(
    pronta ? (enc.modalidade === 'ENTREGA' ? 'ENTREGUE' : 'RETIRADA') : 'DESISTIU',
  );
  const [quem, setQuem] = useState('');
  const [obs, setObs] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const entrega = como === 'RETIRADA' || como === 'ENTREGUE';
  const salvar = async () => {
    setEnviando(true);
    const r = await resolver(sessao, enc.id, {
      como,
      quemRecebeu: entrega ? quem : null,
      obs: obs || null,
    });
    setEnviando(false);
    if (r.ok) aoResolver();
    else
      setErro(
        r.falha.tipo === 'conflito' || r.falha.tipo === 'invalido'
          ? r.falha.mensagem
          : 'Não foi possível resolver.',
      );
  };
  return (
    <Gaveta
      titulo="Resolver encomenda"
      subtitulo={enc.codigo}
      aoFechar={aoFechar}
      largura={520}
      rodape={
        <>
          {erro && <p className="enc-form-erro">{erro}</p>}
          <button type="button" className="enc-botao-sec" onClick={aoFechar}>
            Voltar
          </button>
          <button
            type="button"
            className="enc-botao"
            disabled={enviando}
            onClick={() => void salvar()}
          >
            {enviando ? 'Salvando…' : 'Confirmar'}
          </button>
        </>
      }
    >
      {!pronta && (
        <p className="enc-faixa-aviso">
          A encomenda ainda não está pronta: só dá para registrar desistência, cancelamento ou
          outro.
        </p>
      )}
      <fieldset className="enc-secao">
        <legend>Como foi concluída?</legend>
        <Opcoes
          nome="Como foi concluída"
          valor={como}
          aoMudar={setComo}
          opcoes={[
            ...(pronta
              ? ([
                  ['RETIRADA', 'Retirada pelo cliente'],
                  ['ENTREGUE', 'Entregue no endereço'],
                ] as const)
              : []),
            ['DESISTIU', 'Cliente desistiu'],
            ['CANCELADA', 'Cancelada'],
            ['OUTRO', 'Outro'],
          ]}
        />
      </fieldset>
      {entrega && (
        <Campo rotulo="Quem recebeu" largo>
          <input value={quem} onChange={(e) => setQuem(e.target.value)} maxLength={120} />
        </Campo>
      )}
      <Campo rotulo={entrega ? 'Observação (opcional)' : 'Motivo'} largo>
        <textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={3} maxLength={500} />
      </Campo>
      <p className="enc-campo-dica">Data e hora ficam registradas automaticamente.</p>
    </Gaveta>
  );
}

function Pagar({
  sessao,
  enc,
  aoFechar,
  aoPagar,
}: {
  sessao: Sessao;
  enc: DetalheDaEncomenda;
  aoFechar: () => void;
  aoPagar: () => void;
}) {
  const [valor, setValor] = useState((enc.pagamento.saldo ?? '').replace('.', ','));
  const [forma, setForma] = useState('PIX');
  const [erro, setErro] = useState<string | null>(null);
  const dec = (t: string) => {
    const v = t.replace(/\./g, '').replace(',', '.');
    return /^\d{1,7}(\.\d{1,2})?$/.test(v) ? v : null;
  };
  const salvar = async () => {
    const v = dec(valor);
    if (!v) return setErro('Valor inválido.');
    const r = await pagar(sessao, enc.id, {
      valor: v,
      forma,
      trocoPara: null,
    });
    if (r.ok) aoPagar();
    else setErro(r.falha.tipo === 'invalido' ? r.falha.mensagem : 'Não foi possível registrar.');
  };
  return (
    <Gaveta
      titulo="Registrar pagamento"
      subtitulo={`Saldo ${emReais(enc.pagamento.saldo)}`}
      aoFechar={aoFechar}
      largura={460}
      rodape={
        <>
          {erro && <p className="enc-form-erro">{erro}</p>}
          <button type="button" className="enc-botao-sec" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="enc-botao" onClick={() => void salvar()}>
            Registrar
          </button>
        </>
      }
    >
      <div className="enc-grade-form">
        <Campo rotulo="Valor">
          <input value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" />
        </Campo>
        <Campo rotulo="Forma">
          <select value={forma} onChange={(e) => setForma(e.target.value)}>
            <option value="PIX">Pix</option>
            <option value="DINHEIRO">Dinheiro</option>
            <option value="CREDITO">Cartão crédito</option>
            <option value="DEBITO">Cartão débito</option>
            <option value="OUTRO">Outro</option>
          </select>
        </Campo>
      </div>
    </Gaveta>
  );
}
